<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    ref="storeFinder"
    class="flex flex-col gap-4 storefinder bg-white rounded-3xl border p-4"
    :class="isAppDialog ? '' : 'bg-secondary'"
  >
    <div
      :class="isAppDialog ? 'text-center text-lg font-bold' : 'text-2xl'"
      v-html="title"
    />
    <template v-if="isAppDialog && !isCentralStore">
      <div
        class="border-primary border btn-primary p-4 w-full font-bold text-center"
        @click="$emit('storeChanged', currentStoreId)"
      >
        Danke, die Zuordnung passt,<br >
        Weiter zur Startseite
      </div>
    </template>
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <div class="lg:col-span-5">
        <button
          class="border-primary border btn-primary p-4 w-full rounded-full font-bold"
          @click="checkOwnLocation()"
        >
          {{ currentLocationText }}
        </button>
      </div>
      <div class="lg:col-span-7">
        <template v-if="!fallback">
          <GMapAutocomplete
            v-if="initialized"
            class="p-4 w-full border rounded-full bg-secondary-light font-bold"
            placeholder="Postleitzahl oder Stadt eingeben"
            :select-first-on-enter="true"
            :options="{
              fields: ['place_id', 'name', 'geometry'],
              componentRestrictions: { country: 'de' },
              types: ['(regions)'],
            }"
            @place_changed="setPlace"
          />
        </template>
        <input
          v-else
          type="text"
          placeholder="Fachmarkt Name"
          class="p-4 w-full border rounded-full bg-secondary-light font-bold"
          @change="search"
        >
      </div>
    </div>
    <!-- <storeInformation /> -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <div :class="showMap ? 'lg:col-span-5 order-1' : 'lg:col-span-12 order-3'">
        <PopUpsStoreFinderEntityList
          ref="storeList"
          class="lg:max-h-[500px] lg:overflow-auto"
          :store-list="listOfStores"
          @clicked-on-store="clickedOnStore"
          @store-changed="
            (storeId) => {
              $emit('storeChanged', storeId);
            }
          "
        />
      </div>
      <div class="lg:col-span-7 lg:order-2 order-0 relative">
        <span
          v-if="showMap"
          class="btn-primary absolute z-1 right-1.5 top-1.5 text-3xl font-bold px-5 py-1 cursor-pointer rounded-full flex items-center justify-center"
          @click="showMap = !showMap"
          >−</span
        >
        <a
          v-else
          class="text-primary cursor-pointer"
          @click="showMap = !showMap"
          >Karte anzeigen</a
        >
        <GMapMap
          v-if="initialized"
          :center="center"
          map-type-id="terrain"
          :zoom="zoom"
          class="w-full"
          style="height: 500px"
          :class="showMap ? '' : 'hidden'"
        >
          <GMapMarker
            v-for="(marker, index) in markers"
            :key="index"
            :aria-label="'Ort Marker'"
            :title="'Ortschaft'"
            :icon="'https://cdn.expert.de/static/images/test/pin.png'"
            :position="marker.position"
            :clickable="true"
            @click="storeList ? storeList.selectStore(marker) : () => {}"
          />
        </GMapMap>
        <div
          v-else-if="showMap"
          class="flex min-h-full flex-col justify-center gap-4 p-2"
        >
          <NuxtImg
            class="size-40 mx-auto shake-that-cookie"
            src="ea/e1/6f/785c1f6926bd8e3a672f4bfb7e47e7510e/cookie.svg"
            alt="Cookies"
          />

          <p>
            Um die Kartenansicht sowie die Suche nach Postleitzahl oder Ort nutzen zu können, benötigen wir Ihre
            Zustimmung zur Verwendung von Cookies für externe Dienste (z. B. Google Maps). Bitte aktivieren Sie die
            Marketing-Cookies, um diesen Dienst dauerhaft verwenden zu können.
          </p>

          <p class="mt-4">Alternativ können Sie der Nutzung externer Dienste zur Fachmarktsuche einmalig zustimmen.</p>

          <div class="flex lg:w-2/3 w-full mx-auto justify-center gap-4">
            <button
              type="button"
              class="btn btn-primary w-1/2 rounded-full font-bold"
              @click="changeCookies"
            >
              Einstellungen anpassen
            </button>

            <button
              type="button"
              class="btn btn-primary w-1/2 rounded-full font-bold"
              @click="allowOnce = true"
            >
              Einmalig erlauben
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="text-sm">
      Sie können Ihren Fachmarkt finden, indem Sie unter Nutzung Ihrer IP-Adresse und anderer technischer Daten Ihren
      aktuellen Standort ermitteln lassen oder eine Postleitzahl / Stadt eingeben. Wenn Sie diese Möglichkeiten nicht
      nutzen möchten, ermitteln wir automatisiert Ihren ungefähren Standort, um Sie einem Fachmarkt zuordnen zu können.
      Dies ist für die Nutzung unserer Website / Shop notwendig. Weitere Infos in der Datenschutzerklärung
    </div>
  </div>
</template>
<script setup lang="ts">
import type { SimpleStore, StoreDistance } from '~~/shared/types/Store';
import { useGeolocation } from '@vueuse/core';
import { ProvideNames } from '#shared/statics';

interface Marker {
  id: string;
  position: {
    lat: number;
    lng: number;
  }
}

const props = defineProps({
  title: {
    type: String,
    default: 'Jetzt einen Fachmarkt zuordnen!',
  },
  isAppDialog: {
    type: Boolean,
  },
});

useHead({
  title: 'Finden Sie Ihren Fachmarkt - bei expert',
  meta: [
    {
      name: 'description',
      content:
        'Sie können Ihren expert Fachmarkt finden, indem Sie unter Nutzung Ihrer IP-Adresse und anderer technischer Daten Ihren aktuellen Standort ermitteln lassen oder eine Postleitzahl / Stadt eingeben.',
    },
    { property: 'pagetype', content: 'Storefinder' },
  ],
});

const route = useRoute();
const currentStore = useCurrentStore();
const currentStoreId = computed(() => currentStore.value.id);
const isCentralStore = computed(() => {
  return currentStoreId.value != 'e_2879130';
});
const currentLocationText = ref('Aktuellen Standort ermitteln');
const storeList = useTemplateRef('storeList');
const zoom = ref(8);
const showMap = ref(true);
const fallback = ref(false);

function googleMapsErrorHandler() {
  fallback.value = true;
}

onMounted(() => {
  window.addEventListener('google-maps-auth-error', googleMapsErrorHandler);
});

onBeforeUnmount(() => {
  window.removeEventListener('google-maps-auth-error', googleMapsErrorHandler);
});

defineEmits(['storeChanged']);

const center = ref(currentStore.value?.position ?? ProvideNames.DEFAULT_CENTRAL_STORE.position);
const markers = ref([] as Marker[]);

function clickedOnStore(storeClickedOn?: SimpleStore) {
  zoom.value = 8;
  center.value = {
    lat: 0,
    lng: 0,
  };
  nextTick(() => {
    zoom.value = 11;
    if (storeClickedOn != null && storeClickedOn.latitude && storeClickedOn.longitude) {
      center.value = { lat: storeClickedOn.latitude, lng: storeClickedOn.longitude };
    }
  });
}

const searchPhrase = ref();
const searchPosition = ref(currentStore.value?.position);
watch(
  () => currentStore.value?.position,
  (newPos) => {
    if (newPos != null) {
      if (searchPosition.value?.lat != newPos.lat || searchPosition.value.lng != newPos.lng) {
        searchPosition.value = newPos;
      }
      if (center.value?.lat != newPos.lat || center.value?.lng != newPos.lng) {
        center.value = newPos;
      }
    }
  },
);
const maxResults = ref(10);
const displayNearbyStores = ref(true);

const payback = computed(() =>
  route.query.paybackStore != null
    ? 'true' === (Array.isArray(route.query.paybackStore) ? route.query.paybackStore[0] : route.query.paybackStore)
    : undefined,
);

const baseUrl = useRuntimeConfig().public.apiUrl;
const { data: listOfStores, error: storeError } = await useFetch<Array<StoreDistance>>(`${baseUrl}/api/storeFinder`, {
  key: 'store-finder',
  method: 'GET',
  params: {
    maxResults: maxResults,
    searchPhrase: searchPhrase,
    position: searchPosition,
    payback: payback,
  },
  default: () => []
});

if (storeError.value) {
  console.warn('could not fetch stores', storeError.value);
}

watch(
  listOfStores,
  (distances = []) => {
    center.value = searchPosition.value;
    const firstStore = distances.at(0)?.store;
    if (firstStore != null) {
      markers.value = distances.map((distance) => {
        return {
          id: distance.store.id,
          position: {
            lat: distance.store.latitude,
            lng: distance.store.longitude,
          },
        };
      });
    } else {
      markers.value = [];
    }
  },
  { immediate: true },
);

interface GooglePlace {
  name: string;
  geometry?: {
    location: {
      lat: () => number;
      lng: () => number;
    };
  };
}

function setPlace(param: GooglePlace) {
  if (param != null) {
    displayNearbyStores.value = false;
    searchPhrase.value = param.name;
    const lat = param.geometry?.location?.lat() ?? searchPosition.value?.lat;
    const lng = param.geometry?.location?.lng() ?? searchPosition.value?.lng;

    if (lat != null && lng != null) {
      searchPosition.value = {
        lat: lat,
        lng: lng,
      };
    }
  }
}

const { coords, error, resume, pause, locatedAt } = useGeolocation({ immediate: false });

function checkOwnLocation() {
  resume();
  displayNearbyStores.value = true;
  currentLocationText.value = 'Standort wird ermittelt...';
}

watch(coords, (someVal) => {
  if (!error?.value) {
    searchPosition.value = {
      lng: someVal.longitude,
      lat: someVal.latitude,
    };
    const locd = new Date(locatedAt?.value || Date.now());
    currentLocationText.value =
      'Aktueller Standort zuletzt um '
      + locd.getHours()
      + ':'
      + (locd.getMinutes() < 10 ? '0' : '')
      + locd.getMinutes()
      + ' ermittelt';
    searchPhrase.value = undefined;
  } else {
    currentLocationText.value = 'Konnte Standort nicht ermitteln. ' + error?.value?.message;
    searchPhrase.value = undefined;
  }
  displayNearbyStores.value = true;
  pause();
});

function search(event: Event) {
  if (event.target != null && event.target instanceof HTMLInputElement && event.target.value.length > 2) {
    searchPhrase.value = event.target.value;
  }
}

function changeCookies() {
  Cookiebot.renew();
}

const appConfig = useAppConfig();
const initialized = ref(false);
const allowOnce = ref(false);
const marketingCookie = getCookiePermissions(PermissionMapping.MARKETING);

watch([allowOnce, marketingCookie, () => appConfig?.isAppUser], initializeMap);

onMounted(initializeMap);

function initializeMap() {
  initialized.value = marketingCookie.value || allowOnce.value || appConfig?.isAppUser || props?.isAppDialog == true;
}
</script>

<style scoped lang="postcss">
.shake-that-cookie {
  animation: tilt-n-move-shaking 1s infinite;
}

:deep(div.vue-map-container > div.vue-map) {
  @apply rounded-3xl border;
}
</style>
